import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  X, Upload, Loader2, Sparkles, Check, AlertCircle, FileSpreadsheet,
  Building2, Calendar, Euro, Percent, TrendingUp, ChevronRight, ChevronLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Wizard multi-paso para importar una Oferta TIC desde un Excel usando Claude Sonnet 4.6.
 *   Paso 1: Upload (drag & drop)
 *   Paso 2: Loading (polling del job)
 *   Paso 3: Review editable de la extracción
 *   Paso 4: Confirm & save
 */
const fmtEur = (n) => n == null ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const fmtPct = (n) => n == null ? "—" : `${(Number(n) * 100).toFixed(1)}%`;

export default function OfferAIWizardModal({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setStep(1); setFile(null); setJobId(null); setStatus(null);
      setExtracted(null); setError(null);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
  }, [open]);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Solo se aceptan archivos .xlsx");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Máximo 20 MB");
      return;
    }
    setFile(f);
  };

  const launchExtract = async () => {
    if (!file) return;
    setError(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/offers/ai-wizard/extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJobId(data.job_id);
      setStatus("running");
      setStep(2);
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await axios.get(`${API}/offers/ai-wizard/extract/status/${data.job_id}`);
          setStatus(st.status);
          if (st.status === "done") {
            clearInterval(pollRef.current); pollRef.current = null;
            setExtracted(st.extracted || {});
            setStep(3);
          } else if (st.status === "failed") {
            clearInterval(pollRef.current); pollRef.current = null;
            setError(st.error || "Fallo desconocido");
          }
        } catch (e) { /* soft */ }
      }, 4000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const commit = async () => {
    if (!extracted) return;
    setSaving(true);
    try {
      const payload = {
        metadata: extracted.metadata || {},
        cost_sections: extracted.cost_sections || [],
        business_case: extracted.business_case || null,
        pyg_by_year: extracted.pyg_by_year || null,
        criterios_adjudicacion: extracted.criterios_adjudicacion || null,
        fuente_ods: file?.name || "Wizard Pando",
      };
      const { data } = await axios.post(`${API}/offers/ai-wizard/commit`, payload);
      toast.success(`Oferta creada · CAPEX ${fmtEur(data.capex_total)} · OPEX ${fmtEur(data.opex_total)}`);
      onClose?.();
      navigate(`/ofertas-tic/${data.offer_id}`);
    } catch (e) {
      toast.error("Error guardando: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (!open) return null;

  const md = extracted?.metadata || {};
  const cs = extracted?.cost_sections || [];
  const bc = extracted?.business_case || {};
  const py = extracted?.pyg_by_year || [];
  const capex = cs.filter(s => (s.category || "").toUpperCase() === "CAPEX")
    .reduce((acc, s) => acc + (s.items || []).reduce((a, i) => a + (Number(i.cantidad || 0) * Number(i.precio_unitario || 0)), 0), 0);
  const opex = cs.filter(s => (s.category || "").toUpperCase() === "OPEX")
    .reduce((acc, s) => acc + (s.items || []).reduce((a, i) => a + (Number(i.cantidad || 0) * Number(i.precio_unitario || 0)), 0), 0);

  const updateMd = (k, v) => setExtracted(ex => ({ ...ex, metadata: { ...(ex.metadata || {}), [k]: v } }));

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="offer-ai-wizard-modal">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Wizard Pando · Importar Oferta TIC desde Excel</h2>
              <p className="text-[11px] text-gray-500">Claude Sonnet 4.6 · extrae metadatos, CAPEX/OPEX, business case y P&G plurianual</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="offer-ai-wizard-close" className="text-gray-400 hover:text-gray-700 p-1">
            <X size={20} />
          </button>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-1 py-3 border-b bg-gray-50">
          {[
            { n: 1, label: "Subir" },
            { n: 2, label: "Extraer Pando" },
            { n: 3, label: "Revisar" },
            { n: 4, label: "Confirmar" },
          ].map((s, idx) => (
            <React.Fragment key={s.n}>
              <div className={`flex items-center gap-1.5 text-xs ${step >= s.n ? "text-indigo-700 font-semibold" : "text-gray-400"}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step > s.n ? "bg-emerald-500 text-white" : step === s.n ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-500"}`}>
                  {step > s.n ? <Check size={11}/> : s.n}
                </div>
                {s.label}
              </div>
              {idx < 3 && <ChevronRight size={12} className="text-gray-300 mx-1" />}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* STEP 1 · UPLOAD */}
          {step === 1 && (
            <div className="max-w-xl mx-auto text-center space-y-4" data-testid="wizard-step-upload">
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                className="border-2 border-dashed border-indigo-300 rounded-xl py-10 px-6 cursor-pointer hover:bg-indigo-50/50 transition-colors"
              >
                <FileSpreadsheet size={36} className="mx-auto text-indigo-500 mb-3" />
                <p className="font-semibold text-gray-900">Arrastra un Excel (.xlsx) o haz click</p>
                <p className="text-xs text-gray-500 mt-1">Estudio Económico completo · hasta 20 MB · múltiples hojas soportadas</p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx"
                  data-testid="wizard-file-input"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </div>
              {file && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2 text-sm">
                  <FileSpreadsheet size={16} className="text-emerald-600"/>
                  <span className="flex-1 text-left"><strong>{file.name}</strong> · {(file.size/1024/1024).toFixed(1)} MB</span>
                  <button onClick={() => setFile(null)} className="text-gray-400 hover:text-gray-700"><X size={14}/></button>
                </div>
              )}
              {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14}/>{error}</div>}
              <button
                disabled={!file}
                onClick={launchExtract}
                data-testid="wizard-launch-btn"
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold py-3 rounded-lg disabled:opacity-40 hover:from-indigo-700 hover:to-purple-700 transition-all"
              >
                Extraer con Pando <Sparkles size={14} className="inline ml-1" />
              </button>
            </div>
          )}

          {/* STEP 2 · LOADING */}
          {step === 2 && (
            <div className="max-w-md mx-auto text-center space-y-4 py-12" data-testid="wizard-step-loading">
              <Loader2 size={40} className="animate-spin mx-auto text-indigo-600" />
              <h3 className="font-bold text-gray-900">Claude Sonnet 4.6 está analizando el Excel…</h3>
              <p className="text-sm text-gray-500">Parseando hojas, extrayendo CAPEX/OPEX, business case y P&G plurianual. Puede tardar 10-60 seg según el tamaño.</p>
              <p className="text-xs text-gray-400 font-mono">job: {jobId?.slice(0, 8)}… · status: {status}</p>
              {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 text-left"><strong>Error:</strong> {error}</div>}
            </div>
          )}

          {/* STEP 3 · REVIEW */}
          {step === 3 && extracted && (
            <div className="space-y-4" data-testid="wizard-step-review">
              {/* Metadatos editables */}
              <div className="bg-indigo-50/50 border border-indigo-100 rounded-lg p-4">
                <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider mb-3">Metadatos extraídos <span className="text-gray-400 font-normal normal-case">(editables)</span></p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <MdField label="Título" value={md.title} onChange={v => updateMd("title", v)} testid="wizard-md-title"/>
                  <MdField label="Municipio" value={md.municipality} onChange={v => updateMd("municipality", v)} testid="wizard-md-municipality"/>
                  <MdField label="Expediente" value={md.expediente} onChange={v => updateMd("expediente", v)} testid="wizard-md-expediente"/>
                  <MdField label="Fecha límite" value={md.deadline} onChange={v => updateMd("deadline", v)} type="date" testid="wizard-md-deadline"/>
                  <MdField label="Duración (años)" value={md.duracion_anios} onChange={v => updateMd("duracion_anios", Number(v)||null)} type="number" testid="wizard-md-duracion"/>
                  <MdField label="Convenio" value={md.convenio_aplicacion} onChange={v => updateMd("convenio_aplicacion", v)} testid="wizard-md-convenio"/>
                  <MdField label="Importe total (€)" value={md.importe_licitacion_total} onChange={v => updateMd("importe_licitacion_total", Number(v)||null)} type="number" testid="wizard-md-imptotal"/>
                  <MdField label="Importe anual (€)" value={md.importe_licitacion_anual} onChange={v => updateMd("importe_licitacion_anual", Number(v)||null)} type="number" testid="wizard-md-impanual"/>
                </div>
                {md.description && (
                  <div className="mt-3">
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Descripción</label>
                    <textarea
                      value={md.description}
                      onChange={e => updateMd("description", e.target.value)}
                      rows={4}
                      className="w-full mt-1 border border-indigo-200 rounded px-2 py-1 text-xs"
                    />
                  </div>
                )}
              </div>

              {/* Totales calculados */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={<Building2 size={14}/>} label="Secciones" val={cs.length} color="gray"/>
                <StatCard icon={<Euro size={14}/>} label="CAPEX" val={fmtEur(capex)} color="emerald"/>
                <StatCard icon={<Euro size={14}/>} label="OPEX año 1" val={fmtEur(opex)} color="amber"/>
                <StatCard icon={<TrendingUp size={14}/>} label="EBITDA total" val={fmtEur(bc.ebitda_total)} color="indigo"/>
              </div>

              {/* Business Case */}
              {Object.keys(bc || {}).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <p className="text-xs font-bold text-gray-700 uppercase mb-2">Business case (extraído por Pando)</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <BcItem label="IRR unlevered" val={fmtPct(bc.unlevered_irr)}/>
                    <BcItem label="Money multiple" val={bc.money_multiple ? `${Number(bc.money_multiple).toFixed(2)}x` : "—"}/>
                    <BcItem label="Payback" val={bc.payback_years ? `${Number(bc.payback_years).toFixed(2)} años` : "—"}/>
                    <BcItem label="EBITDA margin" val={fmtPct(bc.ebitda_margin_pct)}/>
                  </div>
                </div>
              )}

              {/* Cost sections preview (read-only, con totales) */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 text-xs font-bold text-gray-700 uppercase">Secciones de coste ({cs.length})</div>
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-500 text-[10px] uppercase sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-1.5">Cat.</th>
                        <th className="text-left px-3 py-1.5">Subcategoría · Sección</th>
                        <th className="text-right px-3 py-1.5">Items</th>
                        <th className="text-right px-3 py-1.5">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {cs.map((s, i) => {
                        const sub = (s.items || []).reduce((a, it) => a + (Number(it.cantidad || 0) * Number(it.precio_unitario || 0)), 0);
                        return (
                          <tr key={i} data-testid={`wizard-section-${i}`}>
                            <td className="px-3 py-1.5">
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${(s.category||"").toUpperCase()==="CAPEX" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{s.category}</span>
                            </td>
                            <td className="px-3 py-1.5">
                              <div className="font-semibold text-gray-800">{s.subcategory}</div>
                              <div className="text-gray-500">{s.section_name}</div>
                            </td>
                            <td className="px-3 py-1.5 text-right text-gray-600">{(s.items || []).length}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{fmtEur(sub)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {py.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 text-xs font-bold text-gray-700 uppercase">P&G plurianual ({py.length} años)</div>
                  <div className="p-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-[10px]">
                    {py.slice(0, 10).map(p => (
                      <div key={p.year} className="bg-gray-50 rounded p-1.5">
                        <div className="font-bold text-gray-700">Año {p.year}</div>
                        <div className="text-gray-500">Ing: {fmtEur(p.ingresos)}</div>
                        <div className="text-gray-500">Opex: {fmtEur(p.opex_total)}</div>
                        <div className="text-emerald-600 font-semibold">EBITDA: {fmtEur(p.ebitda)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(1)} className="text-xs border border-gray-300 px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-50" data-testid="wizard-back-btn">
                  <ChevronLeft size={12} className="inline mr-1"/> Volver
                </button>
                <button onClick={() => setStep(4)} data-testid="wizard-goto-confirm-btn" className="bg-indigo-600 text-white text-xs px-5 py-2 rounded-lg font-semibold hover:bg-indigo-700">
                  Continuar <ChevronRight size={12} className="inline ml-1"/>
                </button>
              </div>
            </div>
          )}

          {/* STEP 4 · CONFIRM */}
          {step === 4 && extracted && (
            <div className="max-w-xl mx-auto space-y-4 text-center py-6" data-testid="wizard-step-confirm">
              <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center mx-auto">
                <Sparkles size={22} className="text-indigo-600"/>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Todo listo · confirmamos?</h3>
              <div className="bg-gray-50 rounded-lg p-4 text-left space-y-1 text-sm">
                <div><strong>Título:</strong> {md.title}</div>
                <div><strong>Municipio:</strong> {md.municipality}</div>
                <div><strong>Expediente:</strong> {md.expediente}</div>
                <div><strong>CAPEX:</strong> <span className="text-emerald-700 font-mono">{fmtEur(capex)}</span> · <strong>OPEX año 1:</strong> <span className="text-amber-700 font-mono">{fmtEur(opex)}</span></div>
                <div><strong>Secciones:</strong> {cs.length} · <strong>P&G:</strong> {py.length} años</div>
              </div>
              <p className="text-xs text-gray-500">La oferta se creará en estado <strong>borrador · pendiente validación TIC</strong>. Podrás editar todos los campos después.</p>
              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(3)} className="text-xs border border-gray-300 px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-50">
                  <ChevronLeft size={12} className="inline mr-1"/> Volver a revisar
                </button>
                <button
                  onClick={commit}
                  disabled={saving}
                  data-testid="wizard-commit-btn"
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm px-6 py-2.5 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 size={14} className="animate-spin"/>}
                  {saving ? "Guardando…" : "Crear oferta"}
                  <Check size={14}/>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MdField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full mt-1 border border-indigo-200 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:border-indigo-500"
      />
    </div>
  );
}

function StatCard({ icon, label, val, color = "gray" }) {
  const map = { gray: "bg-gray-50 text-gray-700", emerald: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-700", indigo: "bg-indigo-50 text-indigo-700" };
  return (
    <div className={`${map[color]} rounded-lg p-2.5 border border-white`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider opacity-75">{icon}{label}</div>
      <div className="text-sm font-bold mt-0.5">{val}</div>
    </div>
  );
}

function BcItem({ label, val }) {
  return (
    <div>
      <div className="text-[9px] uppercase text-gray-400 font-semibold">{label}</div>
      <div className="font-mono text-gray-700 font-semibold">{val}</div>
    </div>
  );
}
